import { useMutation } from "@apollo/client";
import {
  Button,
  CircularProgress,
  Chip,
  Stack,
  Typography,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import gql from "graphql-tag";
import React, { useCallback, useContext, useRef, useState } from "react";
import { updateUser } from "../../../apollo/server";
import UserContext from "../../../context/User";
import FlashMessage from "../../FlashMessage";
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Link as RouterLink } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { useTranslation } from "react-i18next";

const UPDATEUSER = gql`${updateUser}`;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#f9fafb",
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#d1d5db" },
    "&.Mui-focused fieldset": { borderColor: "#FF3008" },
    "&.Mui-disabled": { backgroundColor: "#f3f4f6", color: "#6b7280" },
  },
};

function ProfileCard() {
  const { t } = useTranslation();
  const formRef = useRef(null);
  const [nameError, setNameError] = useState("");
  const { profile } = useContext(UserContext);
  const [error, setError] = useState({});
  const [mutate, { loading }] = useMutation(UPDATEUSER, { onCompleted, onError });

  function onError(e) { setError({ type: "error", message: e.message }); }
  function onCompleted({ updateUser }) {
    if (updateUser) setError({ type: "success", message: "Profile updated" });
  }

  const clearErrors = useCallback(() => {
    setNameError("");
    setError({});
  }, []);

  const handleAction = () => {
    clearErrors();
    const name = formRef.current["name"].value;
    if (!name) {
      setNameError("Name is required");
      return;
    }
    mutate({ variables: { name } });
  };

  const toggleSnackbar = useCallback(() => setError({}), []);

  return (
    <Box sx={{ width: "100%" }}>
      <FlashMessage
        open={Boolean(error.type)}
        severity={error.type}
        alertMessage={error.message}
        handleClose={toggleSnackbar}
      />
      {/* The fields are uncontrolled (defaultValue), which only reads the
          value on mount. Opening Profile before the account had loaded left
          email, name and phone blank for good, while the Verified badge
          (drawn live) still showed. Remount the form once the account is in. */}
      <form ref={formRef} key={profile?._id || "loading"}>
        <Stack gap={2}>
          <Box>
            <TextField
              name="email"
              label="Email"
              defaultValue={profile?.email ?? ""}
              disabled
              fullWidth
              size="small"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><EmailIcon sx={{ color: "#6b7280", fontSize: 18 }} /></InputAdornment>
                ),
              }}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
              <Button
                component={RouterLink}
                to="/change-email"
                size="small"
                sx={{ textTransform: "none", color: "#FF3008", fontWeight: 700, fontSize: "0.78rem" }}
              >
                Change email
              </Button>
            </Stack>
          </Box>
          <TextField
            name="name"
            label="Full Name"
            defaultValue={profile?.name ?? ""}
            error={Boolean(nameError)}
            helperText={nameError}
            fullWidth
            size="small"
            sx={fieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><PersonIcon sx={{ color: "#6b7280", fontSize: 18 }} /></InputAdornment>
              ),
            }}
          />
          <Box>
            <TextField
              name="phone"
              label="Mobile Number"
              defaultValue={profile?.phone ?? ""}
              disabled
              fullWidth
              size="small"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><PhoneIcon sx={{ color: "#6b7280", fontSize: 18 }} /></InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {profile?.phoneIsVerified ? (
                      <Chip
                        size="small"
                        icon={<VerifiedIcon sx={{ fontSize: 14, color: "#16a34a !important" }} />}
                        label="Verified"
                        sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700, backgroundColor: "#dcfce7", color: "#16a34a" }}
                      />
                    ) : profile?.phone ? (
                      <Chip
                        size="small"
                        icon={<ErrorOutlineIcon sx={{ fontSize: 14, color: "#dc2626 !important" }} />}
                        label="Verify"
                        component={RouterLink}
                        to="/verify-phone"
                        clickable
                        sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700, backgroundColor: "#fef2f2", color: "#dc2626", cursor: "pointer", textDecoration: "none" }}
                      />
                    ) : null}
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
              <Button
                component={RouterLink}
                to="/change-phone"
                size="small"
                sx={{ textTransform: "none", color: "#FF3008", fontWeight: 700, fontSize: "0.78rem" }}
              >
                {profile?.phone ? "Change number" : "Add number"}
              </Button>
            </Stack>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
            <Button
              disableElevation
              disabled={loading}
              variant="contained"
              onClick={(e) => { e.preventDefault(); handleAction(); }}
              sx={{
                backgroundColor: "#FF3008",
                color: "#fff",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                px: 3, py: 1,
                minWidth: 120,
                "&:hover": { backgroundColor: "#e02b00" },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : (t("saveButton") || "Save")}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
}

export default React.memo(ProfileCard);
